export enum WorkflowEvents {
    ADD_WORKFLOW_BLOCK = "Add Workflow Block",
    PREVIEW_WORKFLOW = "Preview Workflow",
    PREVIEW_WORKFLOW_VIDEO = "Preview Workflow Video",
    CREATE_CUSTOM_BLOCK = "Create Custom Block",
    UPDATE_CUSTOM_BLOCK = "Update Custom Block",
    DELETE_CUSTOM_BLOCK = "Delete Custom Block",
    SHOW_CREATE_CUSTOM_BLOCK = "Show Create Custom Block",
    CLICK_EDIT_CUSTOM_BLOCK = "Click Edit Custom Block",
    CLICK_DELETE_CUSTOM_BLOCK = "Click Delete Custom Block",
    WORKFLOW_TEMPLATE_OPENED = "Workflow Template Opened",
    WORKFLOW_CREATION_AUTH_REQUIRED = "Workflow Creation Auth Required",
    WORKFLOW_CREATED = "Workflow Created",
    WORKFLOW_IMPORTED = "Workflow Imported",
    WORKFLOW_DELETED = "Workflow Deleted",
    WORKFLOW_SAVED = "Workflow Saved",
    WORKFLOW_OPENED = "Workflow Opened",
    WORKFLOWS_PAGE_LOADED = "Workflows Page Loaded",
    EDIT_WORKFLOW_BUTTON_CLICKED = "Edit Workflow Button Clicked",
    DELETE_WORKFLOW_BUTTON_CLICKED = "Delete Workflow Button Clicked",
    DUPLICATE_WORKFLOW_BUTTON_CLICKED = "Duplicate Workflow Button Clicked",
    CREATE_WORKFLOW_BUTTON_CLICKED = "Create Workflow Button Clicked",
    EXPLORE_TEMPLATES_BUTTON_CLICKED = "Explore Templates Button Clicked",
    WORKFLOWS_JSON_OPENED = "Workflows JSON Opened",
    WORKFLOWS_JSON_HELP_OFFER_CLICKED = "Workflows JSON Help Offer Clicked",
    WORKFLOW_HELP_LINK = "Workflow Help Link",
    ONBOARDING_WELCOME_SURVEY_SUBMITTED = "Onboarding Welcome Survey Submitted",
    CALENDLY_LINK_CLICKED = "Calendly Link Clicked",
    LOCALHOST_DOWNLOAD_CLICKED = "Localhost Download Clicked",
    LOCALHOST_CLI_CLICKED = "Localhost CLI Clicked",
    LOCAL_WORKFLOW_SESSION_STARTED = "Local Workflow Session Started",
    INTERACTED_WITH_SHARED_WORKFLOW = "Interacted with Shared Workflow",
    WORKFLOWS_AI_ASSISTANT_CHAT = "Workflows AI Assistant Chat",
    WORKFLOWS_AI_ASSISTANT_FEEDBACK = "Workflows AI Assistant Feedback",
    CUSTOM_BLOCK_AI_ASSISTANT_CHAT = "Custom Block AI Assistant Chat",
    HOSTED_API_WITH_GPU_REQUESTED = "Hosted API with GPU Requested",
    WORKFLOW_TEMPLATE_SELECTED = "Workflow Template Selected",
    SHOW_TEMPLATES_BUTTON_CLICKED = "Show Templates Button Clicked",
    MOBILE_WORKFLOW_BUTTON_CLICKED = "Mobile Workflow Button Clicked",
    WORKFLOW_BUILDER_FEEDBACK_OPENED = "Workflow Builder Feedback Opened",
    WORKFLOW_BUILDER_FEEDBACK_SUBMITTED = "Workflow Builder Feedback Submitted",
    WALKTHROUGH_STARTED = "Walkthrough Started",
    WALKTHROUGH_STEP_COMPLETED = "Walkthrough Step Completed",
    WALKTHROUGH_COMPLETED = "Walkthrough Completed",
    WALKTHROUGH_DISMISSED = "Walkthrough Dismissed",
    WALKTHROUGH_FEEDBACK = "Walkthrough Feedback",
    DEPLOY_CODE_SNIPPET_COPIED = "Deploy Code Snippet Copied"
}

export type WorkflowFeedbackCategory = "bug_report" | "feature_request" | "general_feedback";

export type WorkflowEventPayloadMap = {
    [WorkflowEvents.ADD_WORKFLOW_BLOCK]: {
        blockName?: string;
        blockType?: string;
        workflowId?: string;
        workflowUrl?: string;
        workflowName?: string;
    };
    [WorkflowEvents.PREVIEW_WORKFLOW]: {
        workflowId?: string;
        workflowUrl?: string;
        serverUrl?: string;
        status?: "success" | "error" | string;
        hasInstantModel?: boolean;
        error?: string | null;
        inner_error?: string | null;
        blockCount?: number;
        blocksUsed?: Record<string, number> | string;
    };
    [WorkflowEvents.PREVIEW_WORKFLOW_VIDEO]: {
        sessionId?: string;
        status?: string;
        videoType?: string;
        workflowId?: string;
        serverUrl?: string;
        serverless?: boolean;
        pipelineId?: string;
        error?: string;
        reason?: string;
        duration?: number;
        fps?: number;
    };
    [WorkflowEvents.CREATE_CUSTOM_BLOCK]: {
        workflowId?: string;
        blockType?: string;
        description?: string;
        config?: string;
        status?: string;
        error?: string;
    };
    [WorkflowEvents.UPDATE_CUSTOM_BLOCK]: {
        workflowId?: string;
        blockType?: string;
        description?: string;
        config?: string;
        status?: string;
        error?: string;
    };
    [WorkflowEvents.DELETE_CUSTOM_BLOCK]: {
        workflowId?: string;
        blockType?: string;
        status?: string;
        error?: string;
    };
    [WorkflowEvents.SHOW_CREATE_CUSTOM_BLOCK]: {
        workflowId?: string;
        location?: string;
        isHostedAPI?: boolean;
    };
    [WorkflowEvents.CLICK_EDIT_CUSTOM_BLOCK]: {
        workflowId?: string;
        blockType?: string;
        location?: string;
        isHostedAPI?: boolean;
    };
    [WorkflowEvents.CLICK_DELETE_CUSTOM_BLOCK]: {
        workflowId?: string;
        blockType?: string;
        location?: string;
        isHostedAPI?: boolean;
    };
    [WorkflowEvents.WORKFLOW_TEMPLATE_OPENED]: { source?: string; modelType?: string };
    [WorkflowEvents.WORKFLOW_CREATION_AUTH_REQUIRED]: {
        source?: string;
        workflowName?: string;
        workflowUrl?: string;
        modelType?: string;
    };
    [WorkflowEvents.WORKFLOW_CREATED]: {
        workflowName?: string;
        templateOption?: string;
        source?: string;
        forkedFrom?: string | null;
        workflowId?: string;
        folderId?: string;
    };
    [WorkflowEvents.WORKFLOW_IMPORTED]: Record<string, any>;
    [WorkflowEvents.WORKFLOW_DELETED]: { workflowId?: string };
    [WorkflowEvents.WORKFLOW_SAVED]: {
        workflowName?: string;
        workflowId?: string;
        blockCount?: number;
        blocksUsed?: Record<string, number> | string;
        onlySaveNameUrl?: boolean;
        createdByAI?: boolean;
        editedByAI?: boolean;
    };
    [WorkflowEvents.WORKFLOW_OPENED]: {
        workflowId?: string;
        workflowName?: string;
        workflowUrl?: string;
    };
    [WorkflowEvents.WORKFLOWS_PAGE_LOADED]: { numWorkflows?: number; numFolders?: number };
    [WorkflowEvents.EDIT_WORKFLOW_BUTTON_CLICKED]: {
        workflowId?: string;
        workflowName?: string;
        workflowUrl?: string;
    };
    [WorkflowEvents.DELETE_WORKFLOW_BUTTON_CLICKED]: {
        workflowId?: string;
        workflowName?: string;
        workflowUrl?: string;
    };
    [WorkflowEvents.DUPLICATE_WORKFLOW_BUTTON_CLICKED]: {
        workflowId?: string;
        workflowName?: string;
        workflowUrl?: string;
    };
    [WorkflowEvents.CREATE_WORKFLOW_BUTTON_CLICKED]: Record<string, any>;
    [WorkflowEvents.EXPLORE_TEMPLATES_BUTTON_CLICKED]: Record<string, any>;
    [WorkflowEvents.WORKFLOWS_JSON_OPENED]: Record<string, any>;
    [WorkflowEvents.WORKFLOWS_JSON_HELP_OFFER_CLICKED]: Record<string, any>;
    [WorkflowEvents.WORKFLOW_HELP_LINK]: { type?: string };
    [WorkflowEvents.ONBOARDING_WELCOME_SURVEY_SUBMITTED]: Record<string, any>;
    [WorkflowEvents.CALENDLY_LINK_CLICKED]: Record<string, any>;
    [WorkflowEvents.LOCALHOST_DOWNLOAD_CLICKED]: { source?: string };
    [WorkflowEvents.LOCALHOST_CLI_CLICKED]: { source?: string };
    [WorkflowEvents.LOCAL_WORKFLOW_SESSION_STARTED]: { serverUrl?: string };
    [WorkflowEvents.INTERACTED_WITH_SHARED_WORKFLOW]: {
        workflowId?: string;
        workflowName?: string;
        action?: string;
        referer?: string;
        [key: string]: any;
    };
    [WorkflowEvents.WORKFLOWS_AI_ASSISTANT_CHAT]: {
        status?: string;
        workflowId?: string;
        workflowConfigJSON?: string;
        serverUrl?: string;
        threadId?: string;
        userMessage?: string;
        assistantMessage?: string;
        messageIndex?: number;
        workspacePlan?: string;
        error?: string;
    };
    [WorkflowEvents.WORKFLOWS_AI_ASSISTANT_FEEDBACK]: {
        workflowId?: string;
        threadId?: string;
        feedback?: string;
        messageIndex?: number;
        userMessage?: string;
        assistantMessage?: string;
        workspacePlan?: string;
    };
    [WorkflowEvents.CUSTOM_BLOCK_AI_ASSISTANT_CHAT]: {
        status?: string;
        workflowId?: string;
        threadId?: string;
        userMessage?: string;
        assistantMessage?: string;
        messageIndex?: number;
        workspacePlan?: string;
        error?: string;
    };
    [WorkflowEvents.HOSTED_API_WITH_GPU_REQUESTED]: { source?: string };
    [WorkflowEvents.WORKFLOW_TEMPLATE_SELECTED]: {
        workflowId?: string;
        workflowName?: string;
        templateOption?: string;
        source?: string;
    };
    [WorkflowEvents.SHOW_TEMPLATES_BUTTON_CLICKED]: Record<string, any>;
    [WorkflowEvents.MOBILE_WORKFLOW_BUTTON_CLICKED]: Record<string, any>;
    [WorkflowEvents.WORKFLOW_BUILDER_FEEDBACK_OPENED]: {
        type?: "thumbs-up" | "thumbs-down";
        workflowId?: string;
        workflowName?: string;
    };
    [WorkflowEvents.WORKFLOW_BUILDER_FEEDBACK_SUBMITTED]: {
        type: "thumbs-up" | "thumbs-down";
        submitMethod?: "closed-modal" | "submit-button";
        feedback?: string;
        workflowId?: string;
        workflowName?: string;
        blockCount?: number;
        feedbackCategory: WorkflowFeedbackCategory;
    };
    [WorkflowEvents.WALKTHROUGH_STARTED]: {
        walkthroughId?: string;
        walkthroughVersion?: number;
        restarted?: boolean;
    };
    [WorkflowEvents.WALKTHROUGH_STEP_COMPLETED]: {
        walkthroughId?: string;
        walkthroughVersion?: number;
        stepId?: string;
        stepIndex?: number;
    };
    [WorkflowEvents.WALKTHROUGH_COMPLETED]: {
        walkthroughId?: string;
        walkthroughVersion?: number;
        durationMs?: number;
    };
    [WorkflowEvents.WALKTHROUGH_DISMISSED]: {
        walkthroughId?: string;
        walkthroughVersion?: number;
        stepIndex?: number;
    };
    [WorkflowEvents.WALKTHROUGH_FEEDBACK]: {
        walkthroughId?: string;
        feedback: "thumbs-up" | "thumbs-down";
    };
    [WorkflowEvents.DEPLOY_CODE_SNIPPET_COPIED]: {
        deployMode?: "hosted" | "local" | "video_api" | "video_local";
        language?: string;
        workflowUrl?: string;
    };
};

export type TrackWorkflowEvent = <E extends WorkflowEvents>(
    event: E,
    data: WorkflowEventPayloadMap[E]
) => void;
